import { Button } from '@material-ui/core'
import { Box, FormControl, Grid } from '@mui/material'
import { useContext, useEffect } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { sendSupportRequest } from '../../actions/emailActions'
import statesList from '../../data/states'
import { useEmailValidator } from '../../hooks/useEmailValidator'
import { useInput } from '../../hooks/useInput'
import { Context } from '../../providers/ProjectContextProvider'
import AutoCompleteSelect from '../Form/AutoCompleteSelect'
import FormContainer from '../Form/FormContainer'
import SelectInput from '../Form/SelectInput'
import TextAreaInput from '../Form/TextAreaInput'
import TextInput from '../Form/TextInput'
import Message from '../Message/Message'
import ModalPopUp from '../ModalPopUp/ModalPopUp'

enum Category {
  ACCOUNT = 'Account',
  WEB = 'Website',
  PROJECT = 'Project',
  PPM = 'Part, program, or machine',
  OTHER = 'Other',
}

const ContactSupport = () => {
  const dispatch = useDispatch()

  const { loading, error, success } = useSelector(
    (state: RootStateOrAny) => state.emailSupport
  )
  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)

  const { showContactSupport, setShowContactSupport } = useContext(Context)

  const {
    value: email,
    setValue: setEmail,
    bind: bindEmail,
    error: emailError,
    setError: setEmailError,
  } = useInput(userInfo ? userInfo.email : '')
  const {
    value: name,
    setValue: setName,
    bind: bindName,
    error: nameError,
    setError: setNameError,
  } = useInput(userInfo ? userInfo.firstName + ' ' + userInfo.lastName : '')
  const {
    value: message,
    setValue: setMessage,
    bind: bindMessage,
    error: messageError,
    setError: setMessageError,
  } = useInput('')
  const { bind: bindPhone, value: phone } = useInput(
    userInfo && userInfo.phone ? userInfo.phone : ''
  )
  const {
    value: state,
    setValue: setState,
    error: stateError,
    setError: setStateError,
  } = useInput(
    userInfo && userInfo.company && userInfo.company.state
      ? userInfo.company.state
      : ''
  )

  const {
    value: other,
    bind: bindOther,
    error: otherError,
    setError: setOtherError,
  } = useInput('')

  const {
    value: category,
    bind: bindCategory,
    setError: setCategoryError,
    error: categoryError,
  } = useInput('')

  const emailIsValid = useEmailValidator(email as string)

  useEffect(() => {
    if (success) {
      setShowContactSupport(false)
      setEmail('')
      setName('')
      setMessage('')
    }
    // eslint-disable-next-line
  }, [success])

  const handleSubmit = () => {
    if (!name) {
      setNameError('This is a required field.')
    } else {
      setNameError('')
    }

    if (!email) {
      setEmailError('This is a required field.')
    } else if (!emailIsValid) {
      setEmailError('Please enter a valid email.')
    } else {
      setEmailError('')
    }

    if (!state) {
      setStateError('This is a required field.')
    } else {
      setStateError('')
    }

    if (!category) {
      setCategoryError('This is a required field.')
    } else {
      setCategoryError('')
    }

    if (category === Category.OTHER && !other) {
      setOtherError('This is a required field.')
    } else {
      setOtherError('')
    }

    if (!message) {
      setMessageError('This is a required field.')
    } else {
      setMessageError('')
    }

    if (name && emailIsValid && message && state) {
      const form = {
        name,
        email,
        message,
        state,
        category: category === Category.OTHER ? other : category,
        phone,
      }
      dispatch(sendSupportRequest(form))
    }
  }

  return (
    <>
      <ModalPopUp
        title="Contact support"
        setShowModal={setShowContactSupport}
        showModal={showContactSupport}
        variant="small"
      >
        <FormContainer style={{ padding: 0, minHeight: 360 }}>
          <h4 className="m-b-30">Contact support</h4>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextInput
                name="name"
                label="Name"
                bindValue={bindName}
                error={nameError}
              />
            </Grid>

            <Grid item xs={6}>
              <TextInput
                name="email"
                label="Email"
                bindValue={bindEmail}
                error={emailError}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput name="phone" label="Phone" bindValue={bindPhone} />
            </Grid>

            <Grid item xs={3}>
              <AutoCompleteSelect
                label="State"
                name="state"
                options={statesList}
                value={state as string}
                setValue={setState}
                error={stateError}
                minWidth={93}
              />
            </Grid>
            <Grid item xs={6}>
              <SelectInput
                label="Issue category"
                name="category"
                options={[
                  { value: Category.ACCOUNT, label: Category.ACCOUNT },
                  { value: Category.WEB, label: Category.WEB },
                  { value: Category.PROJECT, label: Category.PROJECT },
                  { value: Category.PPM, label: Category.PPM },
                  { value: Category.OTHER, label: Category.OTHER },
                ]}
                bindValue={bindCategory}
                disabled={false}
                error={categoryError}
              />
            </Grid>

            {category === Category.OTHER && (
              <Grid item xs={6}>
                <TextInput
                  name="other"
                  label="Other"
                  bindValue={bindOther}
                  error={otherError}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl
                component="fieldset"
                sx={{ width: '100% !important' }}
              >
                <TextAreaInput
                  name="message"
                  label={'Message'}
                  error={messageError}
                  bindValue={bindMessage}
                  autoComplete="off"
                  fullWidth
                />
              </FormControl>
            </Grid>
          </Grid>

          {error && <Message variant="error">{error}</Message>}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 6 }}>
            <Button
              color="primary"
              variant="outlined"
              className="m-r-24"
              onClick={() => setShowContactSupport(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
            >
              Send
            </Button>
          </Box>
        </FormContainer>
      </ModalPopUp>
    </>
  )
}

export default ContactSupport
